/**
 * @file TransferItemCells.tsx
 * @description Живые ячейки строки перемещения: «Откуда → Куда» и
 * «Количество» (phase19.24 этап 1, шаг 1).
 *
 * Вынесены отдельно, потому что одна и та же логика нужна таблице на `≥lg`
 * (`DocumentItemsTable`) и карточке на `<lg` (`DocumentCard`). Раньше строка
 * была одной `<li>`-сеткой, работавшей и там и там, — из-за неё таблица
 * перемещения не совпадала с остальными документами.
 *
 * Правка идёт на месте (`PATCH /:id/items/:itemId`): по листу размещения
 * кладовщик проставляет ячейки десяткам партий подряд, и «удалить — добавить
 * заново» означало бы тот же ручной ввод, ради которого лист и делался.
 *
 * Количество редактируется только у межскладской строки: внутри склада партия
 * переезжает целиком (W14), поле там read-only с подписью «вся партия».
 */

import { useEffect, useState } from "react";

import { BinScanField } from "../bins/BinScanField";
import type { StorageBinDto } from "../../../lib/api-bins";
import type {
  TransferItemDto,
  UpdateTransferItemPayload,
} from "../../../lib/api-transfers";
import { transferTargetText } from "./transfer-row-text";
import { isIntraWarehouseRow } from "../../../lib/transfer-rows";
import { qtyInputProps, validateQty } from "../../../lib/qty-input";
// Единица счёта одна на форму добавления и на строку документа: разойдись
// они — в одном поле «шт», в другом доли, и итог документа перестал бы биться.
import { TRANSFER_QTY_UNIT } from "./TransferQuantityField";

interface CellProps {
  item: TransferItemDto;
  fromWarehouseId: string;
  /** false — документ проведён или отменён: показываем факт без контролов. */
  editable: boolean;
  busy: boolean;
  onUpdate: (itemId: string, payload: UpdateTransferItemPayload) => void;
}

/**
 * Ячейка «Откуда → Куда»: адрес источника и выбор адреса назначения.
 * `showFrom={false}` — карточка на телефоне: «Откуда:» там уже стоит своей
 * строкой, и висящее «— →» над полем читалось как мусор (приёмка qa4 п. 8).
 */
export function TransferTargetCell({
  item,
  fromWarehouseId,
  bins,
  editable,
  busy,
  onUpdate,
  showFrom = true,
}: CellProps & { bins: StorageBinDto[]; showFrom?: boolean }) {
  const intra = isIntraWarehouseRow(item, fromWarehouseId);
  const from = item.fromBinCode ?? item.fromBin?.code ?? "—";

  return (
    <div className="flex min-w-0 flex-col gap-1">
      {showFrom ? (
        <span className="font-mono text-xs text-on-surface-variant">
          {from} <span aria-label="в">→</span>
        </span>
      ) : null}
      {editable && intra ? (
        // Скан ячейки или уровня (phase19.22, W14-9) — прямо в строке листа.
        <BinScanField
          compact
          warehouseId={fromWarehouseId}
          bins={bins}
          value={item.toBinId ?? ""}
          valueCode={item.toBinCode ?? item.toBin?.code ?? null}
          onChange={(binId) => onUpdate(item.id, { toBinId: binId || null })}
          disabled={busy || bins.length === 0}
        />
      ) : (
        <span
          className={
            intra && !item.toBinId ? "text-sm text-warning" : "text-sm"
          }
        >
          {transferTargetText(item, fromWarehouseId)}
        </span>
      )}
    </div>
  );
}

/** Ячейка «Кол-во»: ввод у межсклада, «вся партия» внутри склада. */
export function TransferQtyCell({
  item,
  fromWarehouseId,
  editable,
  busy,
  onUpdate,
}: CellProps) {
  const intra = isIntraWarehouseRow(item, fromWarehouseId);
  const [quantity, setQuantity] = useState(item.quantity);

  // Сервер пересчитывает количество (например, «вся партия») — показываем его.
  useEffect(() => setQuantity(item.quantity), [item.quantity]);

  if (editable && !intra) {
    return (
      <input
        type="text"
        inputMode={qtyInputProps(TRANSFER_QTY_UNIT).inputMode}
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        onBlur={() => {
          const next = quantity.trim();
          if (!next || next === item.quantity) return;
          // Дробную пачку не отправляем: возвращаем прежнее количество —
          // документ остаётся тем, что подтверждено на складе.
          if (validateQty(next, TRANSFER_QTY_UNIT, { allowZero: false })) {
            setQuantity(item.quantity);
            return;
          }
          onUpdate(item.id, { quantity: next });
        }}
        disabled={busy}
        aria-label="Количество"
        className="min-h-12 w-full rounded-lg bg-surface-lowest px-2 text-right font-mono text-sm ring-ghost focus:outline-none focus:ring-2 focus:ring-primary"
      />
    );
  }

  return (
    <span className="tabular-nums">
      {item.quantity}
      {item.isFullLot ? (
        <span className="ml-1 text-xs text-on-surface-variant">вся партия</span>
      ) : null}
    </span>
  );
}
